import { useNavigation } from "@react-navigation/native";
import {
  Divider,
  Text,
  View,
  useThemeProvider,
} from "@towersystems/roam-common-native";
import { Product } from "@towersystems/roam-common/lib/generated-types";
import { useCallback } from "react";
import { FlatList, ScrollView, StyleSheet } from "react-native";
import { Colors, Spacings } from "react-native-ui-lib";
import { SearchButton } from "../../../../../../../../components";
import ProductModel from "../../../../../../../../utilities/data/model/Product";
import { StockSelectionType } from "../../../../../../../../utilities/stock-count";
import { useCurrentCountContext } from "../../../../../../../../utilities/stock-count/hooks";
import { AllInventoryMovementItemsScene } from "../../../AllInventoryMovementItemsScene";
import { StockCountTotals } from "./types";
import { ProductResourceListItem } from "../../../../../../../products/components/ProductResourceListItem";

export type StockCountContentProps = StockCountTotals & {
  query?: string;
  setSearchFocused: (focus: boolean) => void;
  loading: boolean;
  items: Product[] | ProductModel[];
  showProductView: boolean;
  compact?: boolean;
};

export function StockCountContent({
  allCount,
  setSearchFocused,
  loading,
  items,
  query,
  showProductView,
  compact,
}: StockCountContentProps) {
  const { navigate } = useNavigation<any>();
  const { colors } = useThemeProvider();

  const { updateStockSelection } = useCurrentCountContext();

  const onProductSelected = useCallback(() => {
    setSearchFocused(false);
  }, [setSearchFocused]);

  const onAddNonMatchedProduct = useCallback(() => {
    navigate("CountProductModalRootScreen", {
      screen: "CountNonProductModalScreen",
      params: {
        barcode: query,
      },
    });
  }, [query]);

  const renderProduct = useCallback(
    ({ item }: { item: Product | ProductModel }) => {
      return (
        <ProductResourceListItem
          product={item as any}
          action={() => {
            return updateStockSelection(
              {
                stockIdentifier: {
                  productId: item.id,
                  barcode: item.stockNumber,
                },
                triggerUpdatedAt: true,
                selectionType: StockSelectionType.SEARCH_SUBMIT,
              },
              onProductSelected,
            );
          }}
          actionLabel="Select"
          paddingVertical={Spacings.s4}
        />
      );
    },
    [updateStockSelection, onProductSelected],
  );

  const emptyComponent =
    items && !loading ? <Text>No products to show</Text> : undefined;

  const addNoneProductMarkup =
    !!query && query.length >= 1 ? (
      <View>
        <SearchButton
          title={`Add Non Matched "${query}" Product`}
          secondaryTitle="Product not found in system, press here to add to count."
          onPress={onAddNonMatchedProduct}
        />
        <Divider />
      </View>
    ) : undefined;

  // always render the product view markup on top otherwise it will
  // cause all data to be reloaded when the product view is toggled
  const productViewMarkup = showProductView ? (
    <View
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        backgroundColor: Colors.surface,
      }}
    >
      <ScrollView
        scrollEnabled={!loading}
        style={[
          {
            backgroundColor: colors.surface,
            ...styles,
          },
        ]}
      >
        {addNoneProductMarkup}
        <FlatList
          ItemSeparatorComponent={() => <Divider marginVertical={0} />}
          ListEmptyComponent={emptyComponent}
          refreshing={loading}
          data={items}
          renderItem={renderProduct}
        />
      </ScrollView>
    </View>
  ) : null;

  return (
    <View style={{ flex: 1, marginBottom: 225 }}>
      <AllInventoryMovementItemsScene />
      {productViewMarkup}
    </View>
  );
}

const styles = StyleSheet.create({
  productViewContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999,
  },
});
